import { KnowledgeBaseAPI } from '../../../../olp-api/classes/knowledge-base/knowledge-base-api';
import { ActivityFolder } from '../../interfaces/activity-folder';
import { ActivityState } from '../../interfaces/activity-state';
import { IncompleteActivityState } from '../../interfaces/incomplete-activity-state';

export class ActivityStateFolderManager {
  readonly localStoragePrefix = 'ActivityState_';
  readonly activityKBStorage = 'HTMLActivityStorage';
  readonly completedActivityCap = 5;

  useKb = false;
  kbClient?: KnowledgeBaseAPI;

  /**
   *
   * @param kbClient the KnowledgeBaseAPI class to use to interface with kb,
   *                 pass nothing||undefined if we want to use localStorage
   */
  constructor(kbClient?: KnowledgeBaseAPI) {
    this.useKb = !!kbClient;
    if (kbClient) {
      this.kbClient = kbClient;
    }
  }

  async addIncompleteActivity(
    activityId: string,
    incompleteActivity: IncompleteActivityState
  ): Promise<void> {
    const activityFolder = await this.getActivityFolder(activityId);
    activityFolder.incompleteActivityState = incompleteActivity;
    await this.saveActivityFolder(activityFolder);
  }

  async getIncompleteActivity(
    activityId: string
  ): Promise<IncompleteActivityState | undefined> {
    const folder = await this.getActivityFolder(activityId);
    return folder.incompleteActivityState;
  }

  async getIncompleteActivities(): Promise<IncompleteActivityState[]> {
    const activityFolders = await this.getActivityFolders();
    const filteredFolders = activityFolders
      .filter(folder => folder.incompleteActivityState !== undefined)
      .map(folder => folder.incompleteActivityState);
    return filteredFolders as IncompleteActivityState[];
  }

  async removeIncompleteActivity(activityId: string): Promise<void> {
    const folder = await this.getActivityFolder(activityId);
    folder.incompleteActivityState = undefined;
    await this.saveActivityFolder(folder);
  }

  async addCompleteActivity(completedActivity: ActivityState): Promise<void> {
    const activityFolder = await this.getActivityFolder(
      completedActivity.activityId
    );
    activityFolder.timesCompleted++;
    if (
      !activityFolder.maxStars ||
      (completedActivity.starsEarned &&
        completedActivity.starsEarned > activityFolder.maxStars)
    ) {
      activityFolder.maxStars = completedActivity.starsEarned ?? 0;
    }
    if (
      completedActivity.secondsToComplete < activityFolder.fastestTimeInSecs ||
      activityFolder.fastestTimeInSecs === 0
    ) {
      activityFolder.fastestTimeInSecs = completedActivity.secondsToComplete;
    }
    if (completedActivity.isAssignment) {
      activityFolder.assignmentCompletions.push(completedActivity);
    } else {
      activityFolder.completions = [
        completedActivity,
        ...activityFolder.completions,
      ].slice(0, this.completedActivityCap);
    }
    await this.saveActivityFolder(activityFolder);
  }

  async getCompleteActivities(activityId: string): Promise<ActivityState[]> {
    const activityFolder = await this.getActivityFolder(activityId);
    if (activityFolder.completions.length > 0) {
      return activityFolder.completions;
    } else {
      throw new Error('No completions found');
    }
  }

  async getActivityFolders(): Promise<ActivityFolder[]> {
    if (this.useKb) {
      const allFolders = await this.kbClient?.getSection(
        this.activityKBStorage
      );
      if (allFolders) {
        const folders: ActivityFolder[] = [];
        Object.keys(allFolders).forEach(folder =>
          folders.push(allFolders[folder] as ActivityFolder)
        );
        return folders;
      } else {
        return [];
      }
    } else {
      const keys = Object.keys(localStorage).filter(key =>
        key.includes(this.localStoragePrefix)
      );
      const folders = keys.map(key =>
        JSON.parse(localStorage.getItem(key) as string)
      );
      return folders;
    }
  }

  async getActivityFolder(activityId: string): Promise<ActivityFolder> {
    let activityFolder: ActivityFolder | undefined;
    if (this.useKb) {
      const folder = await this.kbClient?.get(
        this.activityKBStorage,
        activityId
      );
      if (folder) {
        activityFolder = folder as ActivityFolder;
      }
    } else {
      const localFolder = localStorage.getItem(
        this.localStoragePrefix + activityId
      );
      if (localFolder) {
        activityFolder = JSON.parse(localFolder);
      }
    }
    if (!activityFolder) {
      activityFolder = {
        activityId: activityId,
        timesCompleted: 0,
        maxStars: 0,
        fastestTimeInSecs: 0,
        completions: [],
        assignmentCompletions: [],
      };
    }
    return activityFolder;
  }

  async saveActivityFolder(activityFolder: ActivityFolder): Promise<void> {
    if (this.useKb) {
      return this.kbClient?.setAndSync(
        this.activityKBStorage,
        activityFolder.activityId,
        activityFolder
      );
    } else {
      localStorage.setItem(
        this.localStoragePrefix + activityFolder.activityId,
        JSON.stringify(activityFolder)
      );
    }
  }
}
